/**
 * Dashboard population toggles (ROADMAP:40, v1 parity): three independent
 * Live/Demo/Training switches. Deliberately quiet -- it is an admin
 * instrument, not a headline control -- and rendered only for the
 * reports:write internal view (DashboardPage), the same gate as the currency
 * select. Zero selection is impossible: turning the last one off restores
 * Live.
 */
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { DEFAULT_REPORT_TYPES, REPORT_TYPE_OPTIONS } from './reportTypeStorage.ts';

const LABELS: Record<string, string> = { live: 'Live', demo: 'Demo', training: 'Training' };

export interface ReportTypeTogglesProps {
  value: readonly string[];
  onChange: (next: string[]) => void;
}

export function ReportTypeToggles({ value, onChange }: Readonly<ReportTypeTogglesProps>): React.JSX.Element {
  function handleToggle(type: string, checked: boolean): void {
    const next = REPORT_TYPE_OPTIONS.filter((t) => (t === type ? checked : value.includes(t)));
    onChange(next.length > 0 ? [...next] : [...DEFAULT_REPORT_TYPES]);
  }

  // role="group" so the aria-label is actually announced (a bare div's is not).
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
      role="group"
      aria-label="Dashboard population"
    >
      {REPORT_TYPE_OPTIONS.map((type) => (
        <FormControlLabel
          key={type}
          sx={{ mr: 0.5 }}
          control={
            // No input aria-label: the accessible name must BE the visible
            // label text (WCAG label-in-name).
            <Switch size="small" checked={value.includes(type)} onChange={(e) => handleToggle(type, e.target.checked)} />
          }
          label={<Typography variant="caption">{LABELS[type]}</Typography>}
        />
      ))}
    </Box>
  );
}

export default ReportTypeToggles;
